import type { DataProvider } from 'react-admin';
import { AntennaInput } from '@domain/antenna/antenna';
import { AnimationInput } from '@domain/animation/animation';
import { FormationInput } from '@domain/formation';
import { MissionTypeInput } from '@domain/missionType';
import { SupplyInput } from '@domain/supply';
import { MemberNoteInput, MissionNoteInput } from '@domain/notes';
import { ResourceCategory, type ResourceId } from '@wwf/sdk';
import type { OrganizationInput } from '@domain/organization';
import { uploadRaFile } from '@domain/resource';
import { isRaFile } from '@domain/types';
import { MemberInput } from '@domain/member';

export const memberLifecycleCallbacks = {
    resource: 'member',
    beforeSave: async (
        data: MemberInput,
        _dataProvider: DataProvider,
        _resource: string
    ) => {
        if (data.avatarInput) {
            const avatarId = await uploadRaFile(
                data.avatarInput,
                ResourceCategory.members
            );
            if (avatarId) data.avatarId = avatarId as ResourceId;
        }
        delete data.avatarInput;

        return data;
    },
};

export const organizationLifecycle = {
    resource: 'organization',
    beforeSave: async (
        data: OrganizationInput,
        _dataProvider: DataProvider,
        _resource: string
    ) => {
        if (isRaFile(data.logoInput)) {
            const logoId = await uploadRaFile(
                data.logoInput,
                ResourceCategory.organization
            );
            if (logoId) data.logoId = logoId as ResourceId;
        }
        delete data.logoInput;

        if (isRaFile(data.chatbotAssistantAvatarInput)) {
            const id = await uploadRaFile(
                data.chatbotAssistantAvatarInput,
                ResourceCategory.organization
            );
            if (id) data.chatbotAssistantAvatarId = id as ResourceId;
        }
        delete data.chatbotAssistantAvatarInput;

        if (isRaFile(data.mascotHugImageInput)) {
            const id = await uploadRaFile(
                data.mascotHugImageInput,
                ResourceCategory.organization
            );
            if (id) data.mascotHugImageId = id as ResourceId;
        }
        delete data.mascotHugImageInput;

        return data;
    },
};

export const antennaCoverLifecycle = {
    resource: 'antenna',
    beforeSave: async (
        data: AntennaInput,
        _dataProvider: DataProvider,
        _resource: string
    ) => {
        if (data.coverInput) {
            const coverId = await uploadRaFile(
                data.coverInput,
                ResourceCategory.antennas
            );
            if (coverId) data.coverId = coverId as unknown as ResourceId;
        }
        delete data.coverInput;
        return data;
    },
};

export const formationLifecycle = {
    resource: 'formation',
    beforeSave: async (
        data: FormationInput,
        _dataProvider: DataProvider,
        _resource: string
    ) => {
        if (data.coverInput) {
            const coverId = await uploadRaFile(
                data.coverInput,
                ResourceCategory.formations
            );
            if (coverId) data.coverId = coverId as unknown as ResourceId;
        }
        delete data.coverInput;

        return data;
    },
};

export const animationLifecycle = {
    resource: 'animation',
    beforeSave: async (
        data: AnimationInput,
        _dataProvider: DataProvider,
        _resource: string
    ) => {
        if (data.imageInput) {
            const imageId = await uploadRaFile(
                data.imageInput,
                ResourceCategory.animations
            );
            if (imageId) data.imageId = imageId as ResourceId;
        }
        delete data.imageInput;

        return data;
    },
};

export const missionTypeLifecycle = {
    resource: 'missionType',
    beforeSave: async (
        data: MissionTypeInput,
        _dataProvider: DataProvider,
        _resource: string
    ) => {
        if (data.iconInput) {
            const iconId = await uploadRaFile(
                data.iconInput,
                ResourceCategory.missionTypes
            );
            if (iconId) data.iconId = iconId as ResourceId;
        }
        delete data.iconInput;

        return data;
    },
};

export const supplyLifecycle = {
    resource: 'supply',
    beforeSave: async (
        data: SupplyInput,
        _dataProvider: DataProvider,
        _resource: string
    ) => {
        if (data.imageInput) {
            const imageId = await uploadRaFile(
                data.imageInput,
                ResourceCategory.supplies
            );
            if (imageId) data.imageId = imageId as ResourceId;
        }
        delete data.imageInput;

        return data;
    },
};

export const memberNoteLifecycle = {
    resource: 'memberNote',
    beforeSave: async (
        data: MemberNoteInput,
        _dataProvider: DataProvider,
        _resource: string
    ) => {
        const existingIds = data.attachmentIds ?? [];
        if (data.attachmentsInput?.length) {
            const files = data.attachmentsInput.filter(isRaFile);
            const uploaded = await Promise.all(
                files.map(fi => uploadRaFile(fi, ResourceCategory.memberNotes))
            );
            data.attachmentIds = [
                ...existingIds,
                ...uploaded.filter((id): id is string => id != null),
            ] as ResourceId[];
        }
        delete data.attachmentsInput;

        return data;
    },
};

export const missionNoteLifecycle = {
    resource: 'missionNote',
    beforeSave: async (
        data: MissionNoteInput,
        _dataProvider: DataProvider,
        _resource: string
    ) => {
        const existingIds = data.attachmentIds ?? [];
        if (data.attachmentsInput?.length) {
            const files = data.attachmentsInput.filter(isRaFile);
            const uploaded = await Promise.all(
                files.map(fi => uploadRaFile(fi, ResourceCategory.missionNotes))
            );
            data.attachmentIds = [
                ...existingIds,
                ...uploaded.filter((id): id is string => id != null),
            ] as ResourceId[];
        }
        delete data.attachmentsInput;

        return data;
    },
};

export const lifecycleCallbacks = [
    organizationLifecycle,
    antennaCoverLifecycle,
    formationLifecycle,
    animationLifecycle,
    missionTypeLifecycle,
    supplyLifecycle,
    memberLifecycleCallbacks,
    memberNoteLifecycle,
    missionNoteLifecycle,
];
